import { select, selectAll, Selection } from 'd3';
import { Note, OpenSheetMusicDisplay as OSMD, VexFlowGraphicalNote } from 'opensheetmusicdisplay';
import { Dispatch, SetStateAction, useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAsync, useKey, useMeasure } from 'react-use';
import styled from 'styled-components';
import { v4 as uuidv4 } from 'uuid';
import { defaultOpacity, fullOpacity, lowerOpacity, lowOpacity } from './constants';
import MusicSheet from './models/MusicSheet';
import {
  getAbsoluteOffset,
  getChordSize,
  getDuration,
  getOffset,
  getPitch,
  isBetweenOffsets,
  isChord,
  NoteContext,
  translateAccidentalEnumToString,
  translateNoteEnumToString
} from './models/Note';
import { Occurrence, Pattern, PatternLabelGenerator } from './models/Pattern';
import { MeasureElement, Voice } from './models/Voice';
import PatternHighlight, { patternHighlightClass } from './PatternHighlight';
import { SelectionContext } from './SelectionContext';
import { revealEvent } from './scoreFollowing';
import { clampZoom, nextZoom } from './SheetZoom';
import { ThemeContext } from './ThemeContext';

export type BeamStemElementSelection = Selection<SVGGElement, unknown, HTMLElement, any>;
type NoteElementSelection = Selection<SVGGElement, NoteContext, null, undefined>;
type PatternSegments = { color: string; id: string; segments: Segment[] };
type Point = [number, number];
type Segment = {
  occurrenceIndex: number;
  page: SVGElement;
  points: Point[];
  /** The line of music (OSMD's MusicSystem) the segment is drawn in. */
  system?: unknown;
};

export const musicSheetContainerID = 'music-sheet-container';
/**
 * How the sheet is paginated: 'a4' lays the music out once on portrait A4 pages, as printed, on a canvas without
 * scroll bars (at 100 % the whole page fits; zoom in around the pointer and grab the page to move it); 'fit' lays it
 * out again for the view (pages fill it, zooming changes the bars per line).
 */
export type PageMode = 'a4' | 'fit';
// Width of an A4 page in staff spaces (OSMD units): a printed score's staff size of about 7 mm.
const a4PageUnits = 120;
/**
 * The white space around the music of every page, in staff spaces (OSMD units; 'compacttight' leaves almost none, so
 * the notes ran to the edges): an A4 page about 12 mm as printed, a fitted page (which scales with the zoom) less. The
 * top margin is the space above the first staff: notes above it reach into the margin a little.
 */
const pageMargins: Record<PageMode, { bottom: number; side: number; top: number }> = {
  a4: { bottom: 7, side: 7, top: 8 },
  fit: { bottom: 3, side: 3, top: 4 }
};
// The title block of the first page (OSMD units): heights of the title and of the line with artist and year, and the
// space between the block and the first staff (more than a margin: notes above the staff reach into it).
const titleHeight = 3;
const bylineHeight = 2.2;
const titleGap = 6;
// The brace or bracket in front of a system of several staves lies left of the system, in its page's left margin.
const bracketWidth = 1.4;

/** Whether the systems begin with a brace or bracket (several staves, as in piano music, or several instruments). */
const hasBrackets = (openSheetMusicDisplay: OSMD) => {
  const instruments = openSheetMusicDisplay.Sheet?.getVisibleInstruments() ?? [];
  return instruments.length > 1 || instruments.some((instrument) => instrument.Staves.length > 1);
};
// The canvas around an A4 page: its edges never come further into the view than this.
const a4CanvasMargin = 16;
// Dragged this far beyond the edge of a zoomed-in page, the page turns.
const a4TurnOvershoot = 80;
export const musicSheetPaginationClass = 'ul.ant-pagination';
const maximumHoverDistance = 40;
// A pointer that moves farther than this while pressed grabs the page instead of selecting a note.
const grabThreshold = 8;

/** Arrow keys belong to text fields and sliders while they have the focus (audit ISSUE-011). */
const isTyping = (event: KeyboardEvent) => {
  const target = event.target as HTMLElement | null;
  return Boolean(
    target?.closest(
      'input, textarea, select, [contenteditable="true"], [role="slider"], [role="combobox"]'
    )
  );
};

const AnimatedCircle = styled.circle`
  cx: ${(styles) => styles.cx};
  cy: ${(styles) => styles.cy};
  transition-duration: 0.15s;
  transition-property: cx, cy;
`;

const MelodyAnalysis = ({
  currentPage,
  hoveredOccurrenceIndex,
  hoveredOccurrences,
  hoveredPatternIDs,
  isMusicSheetRendered,
  musicSheets,
  noteBeamElements,
  noteElements,
  noteStemElements,
  numberOfPages,
  pageMode,
  patterns,
  selectedMusicSheet,
  setAbsoluteOffsetPageMap,
  setConfiguringID,
  setCurrentPage,
  setHoveredOccurrenceIndex,
  setHoveredPatternIDs,
  setIsMusicSheetRendered,
  setNewPattern,
  setNoteBeamElements,
  setNoteElements,
  setNoteStemElements,
  setNumberOfPages,
  setPatterns,
  setSelectedMusicSheetID,
  setSuggestingID,
  setZoom,
  suggestingID,
  voices,
  zoom
}: {
  currentPage: number;
  hoveredOccurrenceIndex: number | undefined;
  hoveredOccurrences: Occurrence[];
  hoveredPatternIDs: string[];
  isMusicSheetRendered: boolean;
  musicSheets: MusicSheet[];
  noteBeamElements: BeamStemElementSelection;
  noteElements: NoteElementSelection;
  noteStemElements: BeamStemElementSelection;
  numberOfPages: number;
  pageMode: PageMode;
  patterns: Pattern[];
  selectedMusicSheet: MusicSheet;
  setAbsoluteOffsetPageMap: Dispatch<SetStateAction<Map<number, number>>>;
  setConfiguringID: Dispatch<SetStateAction<string | undefined>>;
  setCurrentPage: Dispatch<SetStateAction<number>>;
  setHoveredOccurrenceIndex: Dispatch<SetStateAction<number | undefined>>;
  setHoveredPatternIDs: Dispatch<SetStateAction<string[]>>;
  setIsMusicSheetRendered: Dispatch<SetStateAction<boolean>>;
  setNewPattern: Dispatch<SetStateAction<Pattern>>;
  setNoteBeamElements: Dispatch<SetStateAction<BeamStemElementSelection>>;
  setNoteElements: Dispatch<SetStateAction<NoteElementSelection>>;
  setNoteStemElements: Dispatch<SetStateAction<BeamStemElementSelection>>;
  setNumberOfPages: Dispatch<SetStateAction<number>>;
  setPatterns: Dispatch<SetStateAction<Pattern[]>>;
  setSelectedMusicSheetID: Dispatch<SetStateAction<string>>;
  setSuggestingID: Dispatch<SetStateAction<string>>;
  /** Zoom of the sheet (SheetZoom in the header); the music is laid out again at its size. */
  setZoom: Dispatch<SetStateAction<number>>;
  suggestingID: string;
  voices: Voice[];
  zoom: number;
}) => {
  const numberOfSkeletonPlaceholders = 7;
  const viewportReference = useRef<HTMLDivElement>(null);
  const sheetReference = useRef<HTMLDivElement>(null);
  // The zoom the music is laid out at, the page size it was laid out for and the first note of the page that was on
  // view (to stay there when the music is laid out again).
  const zoomReference = useRef(1);
  const renderedSize = useRef<{ height: number; width: number }>(undefined);
  const readingOffset = useRef<number>(undefined);
  const [renderCount, setRenderCount] = useState(0);
  const currentPageReference = useRef(currentPage);
  currentPageReference.current = currentPage;
  const numberOfPagesReference = useRef(numberOfPages);
  numberOfPagesReference.current = numberOfPages;
  const pageModeReference = useRef(pageMode);
  pageModeReference.current = pageMode;
  // The page mode the music was laid out in.
  const renderedMode = useRef<PageMode>(pageMode);

  // Where the A4 pages lie on the canvas (top left corner, in pixels of the view).
  const pan = useRef({ x: 0, y: 0 });

  const a4Pages = () =>
    Array.from(
      document.querySelectorAll<SVGSVGElement>(`#${musicSheetContainerID} svg[id^=osmdSvgPage]`)
    );

  /** The size of an A4 page at the zoom: at 100 % the whole page fits the canvas. */
  const a4PageSize = () => {
    const viewport = viewportReference.current;
    const viewBox = a4Pages()[0]?.getAttribute('viewBox')?.split(' ').map(Number);
    if (!viewport || !viewBox || !(viewBox[2] > 0)) return undefined;
    const ratio = viewBox[3] / viewBox[2];
    const fitted = Math.min(
      viewport.clientWidth - 2 * a4CanvasMargin,
      (viewport.clientHeight - 2 * a4CanvasMargin) / ratio
    );
    const width = Math.max(fitted, 120) * zoomReference.current;

    return { height: width * ratio, width };
  };

  /**
   * Puts the pages where `pan` says, kept on the canvas: centred along a side where the page is smaller than the view,
   * otherwise its edges never come further in than the margin.
   */
  const placeA4Pages = () => {
    const viewport = viewportReference.current;
    const pages = document.getElementById(musicSheetContainerID);
    const size = a4PageSize();
    if (!viewport || !pages || !size || renderedMode.current !== 'a4') return;
    const keep = (value: number, view: number, extent: number) =>
      extent + 2 * a4CanvasMargin <= view
        ? (view - extent) / 2
        : Math.min(a4CanvasMargin, Math.max(view - extent - a4CanvasMargin, value));
    pan.current = {
      x: keep(pan.current.x, viewport.clientWidth, size.width),
      y: keep(pan.current.y, viewport.clientHeight, size.height)
    };
    pages.style.transform = `translate(${pan.current.x}px, ${pan.current.y}px)`;
  };

  /** Sizes the A4 pages for the zoom and the view; the music on them stays as it was laid out. */
  const sizeA4Pages = () => {
    const pages = document.getElementById(musicSheetContainerID);
    const size = a4PageSize();
    if (!pages || !size || renderedMode.current !== 'a4') return;
    a4Pages().forEach((page) => {
      page.setAttribute('width', String(size.width));
      page.setAttribute('height', String(size.height));
    });
    pages.style.width = `${size.width}px`;
    placeA4Pages();
  };

  /** Zooms the A4 pages around a point of the view (the pointer; by default the middle of the view). */
  const zoomA4At = (target: number, anchor?: { x: number; y: number }) => {
    const viewport = viewportReference.current;
    const before = a4PageSize();
    zoomReference.current = target;
    const after = a4PageSize();
    if (viewport && before && after) {
      const point = anchor ?? { x: viewport.clientWidth / 2, y: viewport.clientHeight / 2 };
      const scale = after.width / before.width;
      pan.current = {
        x: point.x - (point.x - pan.current.x) * scale,
        y: point.y - (point.y - pan.current.y) * scale
      };
    }
    sizeA4Pages();
  };

  /** The top of the page (its left edge when zoomed in): where reading goes on after turning a page. */
  const showTopOfA4Page = () => {
    pan.current = { x: Number.POSITIVE_INFINITY, y: Number.POSITIVE_INFINITY };
    placeA4Pages();
  };

  /** Moves the A4 pages smoothly, e.g. to the notes that sound (score following). */
  const glideA4Pages = (move: () => void) => {
    const pages = document.getElementById(musicSheetContainerID);
    pages?.classList.add('is-gliding');
    move();
    window.setTimeout(() => pages?.classList.remove('is-gliding'), 320);
  };

  const [
    musicSheetContainerReference,
    { height: musicSheetContainerHeight, width: musicSheetContainerWidth }
  ] = useMeasure();
  const [hoveredNote, setHoveredNote] = useState<SVGGElement>();
  const [hoveredNoteContext, setHoveredNoteContext] = useState<NoteContext>();
  const [isHovering, setIsHovering] = useState<boolean>(false);
  const [noteBeamsOnCurrentPage, setNoteBeamsOnCurrentPage] = useState<BeamStemElementSelection>();
  const [noteElementsOnCurrentPage, setNoteElementsOnCurrentPage] =
    useState<NoteElementSelection>();
  const [noteStemsOnCurrentPage, setNoteStemsOnCurrentPage] = useState<BeamStemElementSelection>();
  const [openSheetMusicDisplay, setOpenSheetMusicDisplay] = useState<OSMD>();
  const [patternEndNoteContext, setPatternEndNoteContext] = useState<NoteContext>();
  const [patternHighlights, setPatternHighlights] = useState<PatternSegments[]>([]);
  const [patternStartNoteContext, setPatternStartNoteContext] = useState<NoteContext>();
  const [selection] = useContext(SelectionContext);
  const [theme] = useContext(ThemeContext);
  const [voiceColoringHitSet, setVoiceColoringHitSet] = useState<Set<MeasureElement>>(new Set());

  const highlightSelectedVoice = (noteContextForComparison: NoteContext) => {
    noteElementsOnCurrentPage.attr('opacity', (noteContext) =>
      noteContext.voiceNumber === noteContextForComparison.voiceNumber &&
      getAbsoluteOffset(noteContext.note.sourceNote) >=
        getAbsoluteOffset(noteContextForComparison.note.sourceNote)
        ? fullOpacity
        : lowerOpacity
    );

    const noteElementNumbersInPattern = noteElementsOnCurrentPage
      .filter(
        (noteContext) =>
          noteContext === noteContextForComparison ||
          (noteContext.voiceNumber === noteContextForComparison.voiceNumber &&
            getAbsoluteOffset(noteContext.note.sourceNote) >
              getAbsoluteOffset(noteContextForComparison.note.sourceNote))
      )
      .nodes()
      .map((node) => node.id.match(/\d+/)[0]);
    noteStemsOnCurrentPage.attr('opacity', function () {
      const thisNoteNumber = this.id.match(/\d+/)?.[0];

      return noteElementNumbersInPattern.includes(thisNoteNumber) ? fullOpacity : lowerOpacity;
    });

    noteBeamsOnCurrentPage.attr('opacity', lowerOpacity);
  };

  useEffect(() => {
    // The fugue of the paper's figures is the default; an imported sheet stays selected when the catalogue reloads.
    if (musicSheets.length > 0 && !selectedMusicSheet) {
      setSelectedMusicSheetID(
        musicSheets.find((musicSheet) => musicSheet.title === 'Fugue No. 2 in C Minor (BWV 847)').id
      );
    }
  }, [musicSheets]);

  useAsync(async () => {
    if (selectedMusicSheet) {
      const musicSheetContainer = document.querySelector(
        `#${musicSheetContainerID}`
      ) as HTMLDivElement;
      musicSheetContainer.innerHTML = '';

      setIsMusicSheetRendered(false);
      setOpenSheetMusicDisplay(undefined);
      setPatterns([]);
      setConfiguringID(undefined);
      setCurrentPage(1);
      setHoveredNote(undefined);
      setHoveredNoteContext(undefined);
      setHoveredOccurrenceIndex(undefined);
      setHoveredPatternIDs([]);
      setNewPattern(undefined);
      setNoteBeamElements(undefined);
      setNoteBeamsOnCurrentPage(undefined);
      setNoteElements(undefined);
      setNoteElementsOnCurrentPage(undefined);
      setNoteStemElements(undefined);
      setNoteStemsOnCurrentPage(undefined);
      setNumberOfPages(undefined);
      setPatternStartNoteContext(undefined);
      setPatternEndNoteContext(undefined);
      setPatternHighlights([]);
      setSuggestingID(undefined);
      setVoiceColoringHitSet(new Set());
      zoomReference.current = 1;
      setZoom(1);
      readingOffset.current = undefined;
      PatternLabelGenerator.reset();

      renderedMode.current = pageModeReference.current;
      renderedSize.current = await displayMusicSheet(
        selectedMusicSheet,
        musicSheetContainer,
        setOpenSheetMusicDisplay,
        renderedMode.current
      );
      // The pages are known once OSMD has drawn them. Waiting for the container's measured size (below) stalled in a
      // tab in the background, which the browser does not render: the notes were never mapped, the saved analysis
      // never restored.
      setNumberOfPages(musicSheetContainer.querySelectorAll(':scope > div').length);
      setIsMusicSheetRendered(true);
    }
  }, [selectedMusicSheet]);

  useEffect(() => {
    // Once per rendered sheet (if the pages were not counted after drawing): zooming changes the container's size,
    // which must not return to page 1.
    if (!numberOfPages && musicSheetContainerHeight > 250 && musicSheetContainerWidth > 250) {
      setCurrentPage(1);
      setNumberOfPages(document.querySelectorAll('#music-sheet-container > div').length);
    }
  }, [musicSheetContainerHeight, musicSheetContainerWidth]);

  useEffect(() => {
    if (isMusicSheetRendered && numberOfPages && voices && !noteElements) {
      const elements = getNoteElements(
        openSheetMusicDisplay,
        voices,
        voiceColoringHitSet,
        setAbsoluteOffsetPageMap,
        setVoiceColoringHitSet
      );
      setNoteElements(elements);
      preparePages(numberOfPages, renderedMode.current);
      if (renderedMode.current === 'a4') {
        // A freshly rendered sheet appears in place: a page turn just before (the first page on opening a sheet) would
        // otherwise let it glide in from the corner while its notes are already measured and pointed at.
        document.getElementById(musicSheetContainerID)?.classList.remove('is-gliding');
        pan.current = { x: Number.POSITIVE_INFINITY, y: Number.POSITIVE_INFINITY };
        sizeA4Pages();
      }
      addPatternHighlightContainers(numberOfPages);
      // After the music was laid out again (zoom, size of the view): the page with the music that was on view.
      if (readingOffset.current !== undefined) {
        const page = pageWithOffset(elements, readingOffset.current);
        if (page !== undefined) setCurrentPage(page);
      }
    }
  }, [isMusicSheetRendered, numberOfPages, openSheetMusicDisplay, voices, renderCount]);

  useEffect(() => {
    if (currentPage !== undefined && noteElements) {
      setNoteBeamElements(selectAll('svg[id^=osmdSvgPage] > g .vf-beam[id]'));
      setNoteStemElements(selectAll('svg[id^=osmdSvgPage] > g .vf-stem[id]'));
      setNoteBeamsOnCurrentPage(selectAll(`${getPageIDFromNumber(currentPage)} > g .vf-beam[id]`));
      setNoteElementsOnCurrentPage(
        noteElements.filter(function () {
          return this.ownerSVGElement.id === getPageNameFromNumber(currentPage);
        })
      );
      setNoteStemsOnCurrentPage(selectAll(`${getPageIDFromNumber(currentPage)} > g .vf-stem[id]`));
    }
  }, [currentPage, noteElements]);

  useEffect(() => {
    if (
      openSheetMusicDisplay &&
      noteBeamsOnCurrentPage &&
      noteElementsOnCurrentPage &&
      noteStemsOnCurrentPage &&
      patternStartNoteContext !== undefined &&
      patternEndNoteContext === undefined
    ) {
      highlightSelectedVoice(patternStartNoteContext);
    }
  }, [noteBeamsOnCurrentPage, noteElementsOnCurrentPage, noteStemsOnCurrentPage]);

  useEffect(() => {
    if (openSheetMusicDisplay && noteElementsOnCurrentPage) {
      if (selection.focusingID !== undefined) {
        select(getPageContentsSelector(currentPage)).on('click', null).on('mousemove', null);

        return;
      }

      select(getPageContentsSelector(currentPage))
        .on('click', () => {
          if (hoveredPatternIDs.length > 0) {
            /*
             * We want to configure the shortest pattern because it is the most difficult to interactively select.
             * The longer enclosing patterns can still be configured by hovering near the non-overlapping parts.
             */
            const shortestPatternID = getShortestPatternIDFromIDs(hoveredPatternIDs, patterns);
            setConfiguringID(shortestPatternID);

            return;
          }

          if (hoveredNoteContext === undefined) return;

          if (patternStartNoteContext === undefined) {
            setPatternStartNoteContext(hoveredNoteContext);
            highlightSelectedVoice(hoveredNoteContext);
          } else if (patternEndNoteContext === undefined) {
            setPatternEndNoteContext(hoveredNoteContext);
            setHoveredNote(undefined);
            setHoveredNoteContext(undefined);

            noteBeamElements.attr('opacity', fullOpacity);
            noteElements.attr('opacity', fullOpacity);
            noteStemElements.attr('opacity', fullOpacity);
          }
        })
        .on('mousemove', (event) => {
          const { x, y } = event;

          setIsHovering(true);

          // The note whose head is closest to the pointer. Measuring from the corners of the notes' bounding boxes
          // made the selection jump to notes of other staves (study feedback P5).
          const closest: { context?: NoteContext; distance: number; note?: SVGGElement } = {
            distance: maximumHoverDistance
          };
          // After the first click, only later notes of the same voice can end the pattern (audit ISSUE-005).
          const startOffset = patternStartNoteContext
            ? getAbsoluteOffset(patternStartNoteContext.note.sourceNote)
            : undefined;
          noteElementsOnCurrentPage.each(function (noteContext) {
            if (
              patternStartNoteContext &&
              (noteContext.voiceNumber !== patternStartNoteContext.voiceNumber ||
                getAbsoluteOffset(noteContext.note.sourceNote) <= startOffset)
            )
              return;

            const head = (this.querySelector('g.vf-notehead path') ?? this) as Element;
            const box = head.getBoundingClientRect();
            const distance = Math.hypot(x - (box.x + box.width / 2), y - (box.y + box.height / 2));

            if (distance < closest.distance) {
              closest.context = noteContext;
              closest.distance = distance;
              closest.note = this;
            }
          });
          const hoveredNote = closest.note;
          const hoveredNoteContext = closest.context;

          if (hoveredNote === undefined || hoveredNoteContext === undefined) {
            setHoveredNote(undefined);
            setHoveredNoteContext(undefined);

            return;
          }

          const hasNoVoice = hoveredNoteContext.voiceNumber === undefined;
          const hoveredNoteAbsoluteOffset = getAbsoluteOffset(hoveredNoteContext.note.sourceNote);
          const isRest = hoveredNoteContext.note.sourceNote.isRest();
          const enclosingPatterns = patterns
            .filter((pattern) => pattern.isVisible)
            .filter((pattern) =>
              pattern.occurrences.some(
                (occurrence) =>
                  isBetweenOffsets(
                    hoveredNoteAbsoluteOffset,
                    occurrence.firstOffset,
                    occurrence.lastOffset
                  ) && occurrence.voiceNumber === hoveredNoteContext.voiceNumber
              )
            );

          if (
            (patternStartNoteContext === undefined ||
              (hoveredNoteContext.voiceNumber === patternStartNoteContext?.voiceNumber &&
                hoveredNoteAbsoluteOffset >
                  getAbsoluteOffset(patternStartNoteContext.note.sourceNote))) &&
            !hasNoVoice &&
            !isRest &&
            enclosingPatterns.length === 0
          ) {
            setHoveredNote(hoveredNote);
            setHoveredNoteContext(hoveredNoteContext);
            setHoveredOccurrenceIndex(undefined);
            setHoveredPatternIDs([]);
          } else if (!hasNoVoice && !isRest && enclosingPatterns.length > 0) {
            const absoluteOffset = getAbsoluteOffset(hoveredNoteContext.note.sourceNote);
            const shortestPatternID = getShortestPatternIDFromIDs(
              enclosingPatterns.map((pattern) => pattern.id),
              patterns
            );
            const shortestPattern = patterns.find((pattern) => pattern.id === shortestPatternID);

            if (shortestPattern) {
              setHoveredOccurrenceIndex(
                shortestPattern.occurrences.findIndex(
                  (occurrence) =>
                    occurrence.voiceNumber === hoveredNoteContext.voiceNumber &&
                    isBetweenOffsets(absoluteOffset, occurrence.firstOffset, occurrence.lastOffset)
                )
              );
              setHoveredPatternIDs([shortestPatternID]);
            } else {
              setHoveredPatternIDs([]);
            }

            setHoveredNote(undefined);
            setHoveredNoteContext(undefined);
          } else if (hasNoVoice || isRest || enclosingPatterns.length > 0) {
            setHoveredNote(undefined);
            setHoveredNoteContext(undefined);
            setHoveredOccurrenceIndex(undefined);
            setHoveredPatternIDs([]);
          }
        })
        .on('mouseleave', () => {
          setHoveredNote(undefined);
          setHoveredNoteContext(undefined);
          setHoveredOccurrenceIndex(undefined);
          setHoveredPatternIDs([]);
          setIsHovering(false);
        });
    }
  }, [
    currentPage,
    hoveredNote,
    hoveredNoteContext,
    hoveredPatternIDs,
    noteBeamsOnCurrentPage,
    noteElementsOnCurrentPage,
    noteStemsOnCurrentPage,
    openSheetMusicDisplay,
    patterns,
    patternStartNoteContext,
    patternEndNoteContext,
    selection.focusingID
  ]);

  useKey(
    (event) => event.key === 'Escape',
    () => {
      if (patternStartNoteContext !== undefined && patternEndNoteContext === undefined) {
        setHoveredNote(undefined);
        setHoveredNoteContext(undefined);
        setPatternStartNoteContext(undefined);

        noteBeamElements.attr('opacity', fullOpacity);
        noteElements.attr('opacity', fullOpacity);
        noteStemElements.attr('opacity', fullOpacity);
      }
    },
    { event: 'keyup' }
  );

  useEffect(() => {
    if (openSheetMusicDisplay && noteElements) {
      noteElements
        .selectAll('g.vf-note g.vf-notehead path')
        // Replicate the note context for each path in the note, e.g., for each note in a chord.
        .data((noteContext, _, paths) => Array.from(paths).map(() => noteContext))
        .attr('fill', (noteContext) =>
          theme.colorVoices
            ? voices.find((voice) => voice.number === noteContext.voiceNumber)?.color
            : 'black'
        );

      if (theme.colorVoices) {
        selectAll(`.${patternHighlightClass}`).lower();
      } else {
        selectAll(`.${patternHighlightClass}`).raise();
      }
    }
  }, [theme.colorVoices, noteElements, openSheetMusicDisplay, voices]);

  useEffect(() => {
    if (selection.focusingID === undefined) {
      select(getPageContentsSelector(currentPage)).classed('cursor-default', false);
      select(getPageContentsSelector(currentPage)).classed('cursor-pointer', true);
    } else {
      select(getPageContentsSelector(currentPage)).classed('cursor-default', true);
      select(getPageContentsSelector(currentPage)).classed('cursor-pointer', false);
    }
  }, [selection.focusingID]);

  useEffect(() => {
    if (patternStartNoteContext !== undefined && patternEndNoteContext !== undefined) {
      const firstOffset = getAbsoluteOffset(patternStartNoteContext.note.sourceNote);
      const lastOffset = getAbsoluteOffset(patternEndNoteContext.note.sourceNote);
      const voiceNumber = patternStartNoteContext.voiceNumber;

      const noteContexts = noteElements
        .data()
        .filter((noteContext) => noteContext.voiceNumber === voiceNumber);
      const patternLength =
        noteContexts.findIndex((noteContext) => noteContext === patternEndNoteContext) -
        noteContexts.findIndex((noteContext) => noteContext === patternStartNoteContext) +
        1;
      const newPattern: Pattern = {
        color: theme.colorByVoice
          ? theme.getVoiceColor(voiceNumber)
          : theme.availablePatternColors.pop(),
        connectedToByOccurrences: [],
        connectedToByOperator: [],
        description: undefined,
        hasBeenQueried: false,
        id: uuidv4(),
        isLocked: false,
        isUserDefined: true,
        isVisible: true,
        label: PatternLabelGenerator.next(),
        length: patternLength,
        occurrences: [{ firstOffset, lastOffset, voiceNumber }],
        uniqueOccurrencesCount: 1,
        voiceNumber
      };

      setNewPattern(newPattern);
      // A pattern whose suggestions are still open stays: selecting the next pattern used to delete it silently.
      setPatterns((oldPatterns) => [...oldPatterns, newPattern]);
      setConfiguringID(newPattern.id);
      setSuggestingID(newPattern.id);
      setPatternStartNoteContext(undefined);
      setPatternEndNoteContext(undefined);
    }
  }, [patternEndNoteContext, patternStartNoteContext]);

  useEffect(() => {
    if (openSheetMusicDisplay && noteElements) {
      // Also while voices are coloured; only the stacking order differs (audit ISSUE-013).
      setPatternHighlights(computePatternHighlights(noteElements, patterns, selection.focusingID));

      if (patterns.length === 0) PatternLabelGenerator.reset();
    }
  }, [patterns, selection.focusingID, noteElements]);

  useKey(
    (event) => event.key === 'ArrowRight' && !isTyping(event),
    () => {
      if (currentPage < numberOfPages) {
        setCurrentPage(currentPage + 1);
      }
    },
    { event: 'keyup' }
  );

  useKey(
    (event) => event.key === 'ArrowLeft' && !isTyping(event),
    () => {
      if (currentPage > 1) {
        setCurrentPage(currentPage - 1);
      }
    },
    { event: 'keyup' }
  );

  useEffect(() => {
    for (let pageNumber = 1; pageNumber <= numberOfPages; pageNumber++) {
      const pageContainer = document.querySelector(`#osmdCanvasPage${pageNumber}`);

      if (pageContainer === null) continue;

      // All pages share one place (layout.css); only the current one shows and takes the pointer. The others keep
      // their layout, so their highlights and notes can still be measured.
      (pageContainer as HTMLElement).style.visibility =
        pageNumber === currentPage ? 'visible' : 'hidden';
    }
  }, [currentPage, numberOfPages, renderCount]);

  useEffect(() => {
    if (renderedMode.current === 'a4') glideA4Pages(showTopOfA4Page);
  }, [currentPage]);

  /**
   * Lays the music out again at the zoom, with pages that fill the view: zooming in gives fewer bars per line and more
   * pages, never a sideways scroll. The page with the music that was on view stays on view. Unless `force`, only when
   * the view changed its size.
   */
  const layOut = (force: boolean) => {
    const pages = document.querySelector<HTMLElement>(`#${musicSheetContainerID}`);
    if (!openSheetMusicDisplay || !isMusicSheetRendered || !pages) return;

    const mode = pageModeReference.current;
    // An A4 page keeps its music when the view changes its size: it is only scaled.
    if (!force && mode === 'a4' && renderedMode.current === 'a4') {
      sizeA4Pages();
      return;
    }

    // A4 gave the pages a width of their own (sizeA4Pages); fitted pages take the view's.
    if (mode === 'fit') pages.style.width = '';
    const size = pageFormatOf(pages);
    const previous = renderedSize.current;
    if (
      !force &&
      previous &&
      Math.abs(size.width - previous.width) < 8 &&
      Math.abs(size.height - previous.height) < 8
    )
      return;

    const shown =
      noteElementsOnCurrentPage
        ?.data()
        .map((noteContext) => getAbsoluteOffset(noteContext.note.sourceNote)) ?? [];
    if (shown.length > 0) readingOffset.current = Math.min(...shown);
    // The notes are drawn anew: a selection in progress and everything bound to the old notes go.
    setPatternStartNoteContext(undefined);
    setPatternEndNoteContext(undefined);
    setHoveredNote(undefined);
    setHoveredNoteContext(undefined);
    setNoteElements(undefined);
    setNoteElementsOnCurrentPage(undefined);
    setNoteBeamElements(undefined);
    setNoteBeamsOnCurrentPage(undefined);
    setNoteStemElements(undefined);
    setNoteStemsOnCurrentPage(undefined);
    setPatternHighlights([]);
    setVoiceColoringHitSet(new Set());

    applyPageLayout(openSheetMusicDisplay, pages, mode, size, zoomReference.current);
    openSheetMusicDisplay.render();
    renderedMode.current = mode;
    renderedSize.current = size;
    pages.style.transform = '';
    setNumberOfPages(pages.querySelectorAll(':scope > div').length);
    setRenderCount((count) => count + 1);
  };
  const layOutReference = useRef(layOut);
  layOutReference.current = layOut;

  useEffect(() => {
    // An A4 page is only scaled, around the middle of the view (the wheel zooms it around the pointer itself).
    if (renderedMode.current === 'a4' && openSheetMusicDisplay && isMusicSheetRendered) {
      if (Math.abs(zoom - zoomReference.current) > 1e-3) zoomA4At(zoom);
      return;
    }
    zoomReference.current = zoom;
    if (
      openSheetMusicDisplay &&
      isMusicSheetRendered &&
      Math.abs(openSheetMusicDisplay.Zoom - zoom) > 1e-3
    )
      layOut(true);
  }, [zoom]);

  useEffect(() => {
    if (openSheetMusicDisplay && isMusicSheetRendered && renderedMode.current !== pageMode)
      layOut(true);
  }, [pageMode]);

  // Ctrl (or Cmd) and the mouse wheel, or pinching on a touchpad, zoom instead of the browser. While the wheel turns,
  // fitted pages are scaled around the pointer as a preview and the music is laid out when it rests; an A4 page zooms
  // around the pointer at once. On the A4 canvas the wheel alone moves the page (Shift: sideways).
  useEffect(() => {
    const viewport = viewportReference.current;
    if (!viewport) return;
    let target: number | undefined;
    let timer: number | undefined;

    const onWheel = (event: WheelEvent) => {
      if (renderedMode.current === 'a4' && pageModeReference.current === 'a4') {
        const bounds = viewport.getBoundingClientRect();
        if (event.ctrlKey || event.metaKey) {
          event.preventDefault();
          target = clampZoom((target ?? zoomReference.current) * Math.exp(-event.deltaY * 0.002));
          zoomA4At(Math.round(target * 100) / 100, {
            x: event.clientX - bounds.left,
            y: event.clientY - bounds.top
          });
          window.clearTimeout(timer);
          timer = window.setTimeout(() => {
            target = undefined;
            setZoom(zoomReference.current);
          }, 150);
          return;
        }
        const unit = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? viewport.clientHeight : 1;
        const dx = (event.shiftKey ? event.deltaY : event.deltaX) * unit;
        const dy = (event.shiftKey ? 0 : event.deltaY) * unit;
        const before = { ...pan.current };
        pan.current = { x: pan.current.x - dx, y: pan.current.y - dy };
        placeA4Pages();
        // A page that fits the view does not move: the wheel then scrolls what is around the sheet.
        if (pan.current.x !== before.x || pan.current.y !== before.y) event.preventDefault();
        return;
      }
      if (!event.ctrlKey && !event.metaKey) return;
      event.preventDefault();
      target = clampZoom((target ?? zoomReference.current) * Math.exp(-event.deltaY * 0.002));
      const pages = viewport.firstElementChild as HTMLElement | null;
      if (pages) {
        const bounds = viewport.getBoundingClientRect();
        pages.style.transformOrigin = `${event.clientX - bounds.left}px ${event.clientY - bounds.top + viewport.scrollTop}px`;
        pages.style.transform = `scale(${target / zoomReference.current})`;
      }
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        const zoomed = Math.round((target ?? zoomReference.current) * 100) / 100;
        target = undefined;
        if (Math.abs(zoomed - zoomReference.current) < 0.005) {
          if (pages) pages.style.transform = '';
        } else setZoom(zoomed);
      }, 250);
    };
    viewport.addEventListener('wheel', onWheel, { passive: false });

    return () => {
      viewport.removeEventListener('wheel', onWheel);
      window.clearTimeout(timer);
    };
  }, []);

  // A page fills the view: when the view changes its size (window, browser zoom, layout), the music is laid out again
  // (an A4 page is sized anew).
  useEffect(() => {
    const sheet = sheetReference.current;
    if (!sheet) return;
    let timer: number | undefined;
    const observer = new ResizeObserver(() => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => layOutReference.current(false), 250);
    });
    observer.observe(sheet);

    return () => {
      observer.disconnect();
      window.clearTimeout(timer);
    };
  }, []);

  useKey(
    (event) =>
      ['+', '-', '0'].includes(event.key) &&
      !event.ctrlKey &&
      !event.metaKey &&
      !event.altKey &&
      !isTyping(event),
    (event) => {
      if (event.key === '+') setZoom(nextZoom(zoomReference.current, 1));
      else if (event.key === '-') setZoom(nextZoom(zoomReference.current, -1));
      else setZoom(1);
    }
  );

  // Grabbing the page: dragged sideways it turns like a page of a book (next page to the left); a page taller than the
  // view (zoomed in) moves up and down with it. On the A4 canvas a page larger than the view moves with the pointer in
  // every direction, and turns when it is dragged on beyond its edge. A click without moving still selects notes.
  useEffect(() => {
    const viewport = viewportReference.current;
    if (!viewport) return;
    let start:
      | {
          pan: { x: number; y: number };
          pointerId: number;
          scrollTop: number;
          time: number;
          x: number;
          y: number;
        }
      | undefined;
    let isGrabbing = false;
    let wasGrabbed = false;
    // How far a zoomed-in A4 page was dragged beyond its edge (negative: to the left).
    let overshoot = 0;
    const page = () =>
      document.querySelector<HTMLElement>(`#osmdCanvasPage${currentPageReference.current}`);
    const isA4 = () => renderedMode.current === 'a4' && pageModeReference.current === 'a4';
    // A page that fits the view sideways turns when dragged sideways; a larger one moves.
    const fitsSideways = () => {
      const size = a4PageSize();
      return !size || size.width + 2 * a4CanvasMargin <= viewport.clientWidth;
    };
    const isAtEnd = (dx: number) =>
      (dx > 0 && currentPageReference.current <= 1) ||
      (dx < 0 && currentPageReference.current >= (numberOfPagesReference.current ?? 1));

    const onPointerDown = (event: PointerEvent) => {
      if (event.button !== 0) return;
      start = {
        pan: { ...pan.current },
        pointerId: event.pointerId,
        scrollTop: viewport.scrollTop,
        time: event.timeStamp,
        x: event.clientX,
        y: event.clientY
      };
      isGrabbing = false;
      overshoot = 0;
    };
    const onPointerMove = (event: PointerEvent) => {
      if (!start || event.pointerId !== start.pointerId) return;
      const dx = event.clientX - start.x;
      const dy = event.clientY - start.y;
      if (!isGrabbing && Math.hypot(dx, dy) < grabThreshold) return;
      if (!isGrabbing) {
        isGrabbing = true;
        viewport.setPointerCapture(event.pointerId);
        viewport.classList.add('is-grabbing');
      }
      const grabbed = page();
      if (isA4()) {
        const turnsSideways = fitsSideways();
        const wanted = { x: turnsSideways ? pan.current.x : start.pan.x + dx, y: start.pan.y + dy };
        pan.current = wanted;
        placeA4Pages();
        // Beyond its edge the page follows halfway (a quarter at the first and the last page).
        overshoot = turnsSideways ? dx : wanted.x - pan.current.x;
        const follows = overshoot / (isAtEnd(overshoot) ? 4 : turnsSideways ? 1 : 2);
        if (grabbed) grabbed.style.transform = overshoot ? `translateX(${follows}px)` : '';
        return;
      }
      // The first and the last page resist: there is no page to turn to.
      if (grabbed) grabbed.style.transform = `translateX(${isAtEnd(dx) ? dx / 4 : dx}px)`;
      viewport.scrollTop = start.scrollTop - dy;
    };
    const onPointerUp = (event: PointerEvent) => {
      if (!start || event.pointerId !== start.pointerId) return;
      if (isGrabbing) {
        const isZoomedIn = isA4() && !fitsSideways();
        const dx = isA4() ? overshoot : event.clientX - start.x;
        const speed = Math.abs(dx) / Math.max(1, event.timeStamp - start.time);
        const turns = isZoomedIn
          ? Math.abs(dx) > a4TurnOvershoot
          : Math.abs(dx) > Math.min(120, viewport.clientWidth * 0.15) ||
            (Math.abs(dx) > 30 && speed > 0.5);
        const grabbed = page();
        if (grabbed) grabbed.style.transform = '';
        viewport.classList.remove('is-grabbing');
        wasGrabbed = true;
        if (turns && dx < 0)
          setCurrentPage((current) => Math.min(current + 1, numberOfPagesReference.current ?? 1));
        if (turns && dx > 0) setCurrentPage((current) => Math.max(current - 1, 1));
      }
      start = undefined;
      isGrabbing = false;
    };
    // Releasing a grabbed page ends with a click, which must not select a note.
    const onClick = (event: MouseEvent) => {
      if (!wasGrabbed) return;
      wasGrabbed = false;
      event.stopPropagation();
      event.preventDefault();
    };

    // Score following on the A4 canvas: the page moves so that the notes that sound are in the middle of the view.
    const onReveal = (event: Event) => {
      if (!isA4()) return;
      const view = viewport.getBoundingClientRect();
      const note = (event as CustomEvent<Element>).detail.getBoundingClientRect();
      glideA4Pages(() => {
        pan.current = {
          x: pan.current.x + view.left + view.width / 2 - (note.left + note.width / 2),
          y: pan.current.y + view.top + view.height / 2 - (note.top + note.height / 2)
        };
        placeA4Pages();
      });
    };

    viewport.addEventListener('pointerdown', onPointerDown);
    viewport.addEventListener('pointermove', onPointerMove);
    viewport.addEventListener('pointerup', onPointerUp);
    viewport.addEventListener('pointercancel', onPointerUp);
    viewport.addEventListener('click', onClick, true);
    viewport.addEventListener(revealEvent, onReveal);

    return () => {
      viewport.removeEventListener('pointerdown', onPointerDown);
      viewport.removeEventListener('pointermove', onPointerMove);
      viewport.removeEventListener('pointerup', onPointerUp);
      viewport.removeEventListener('pointercancel', onPointerUp);
      viewport.removeEventListener('click', onClick, true);
      viewport.removeEventListener(revealEvent, onReveal);
    };
  }, []);

  return (
    <div
      className="relative flex min-h-0 flex-col text-center"
      ref={sheetReference}
      style={{ flex: 10 }}
    >
      {!isMusicSheetRendered ? (
        <div className="flex flex-col items-center animate-pulse">
          {[...Array(numberOfSkeletonPlaceholders)].map((_, index) => (
            <div className="w-4/5 h-24 mb-3 mt-3 rounded-md bg-gray-200" key={index}></div>
          ))}
        </div>
      ) : undefined}
      <div
        className={`melodyvis-sheet-viewport min-h-0 ${pageMode === 'a4' ? 'is-a4' : ''} ${isMusicSheetRendered ? 'flex-1' : 'h-0 invisible'}`}
        ref={viewportReference}
      >
        <div
          className="melodyvis-sheet-pages"
          id={musicSheetContainerID}
          ref={musicSheetContainerReference}
        ></div>
      </div>
      <HoveredNoteIndicator
        currentPage={currentPage}
        hoveredNote={hoveredNote}
        hoveredNoteContext={hoveredNoteContext}
      />
      {patternHighlights.map((patternHighlight) => {
        // The highlights are recomputed after the patterns changed: for one render they may still list a pattern
        // that was just discarded or deleted.
        const pattern = patterns.find((candidate) => candidate.id === patternHighlight.id);
        if (pattern === undefined) return null;

        return patternHighlight.segments.map((segment, segmentIndex) => (
          <PatternHighlight
            color={patternHighlight.color}
            colorOpacity={theme.colorVoices ? lowOpacity : defaultOpacity}
            coordinates={segment.points}
            hasEllipsisLeft={
              segmentIndex > 0 &&
              patternHighlight.segments[segmentIndex - 1].occurrenceIndex ===
                segment.occurrenceIndex
            }
            hasEllipsisRight={
              segmentIndex < patternHighlight.segments.length - 1 &&
              patternHighlight.segments[segmentIndex + 1].occurrenceIndex ===
                segment.occurrenceIndex
            }
            hoveredOccurrenceIndex={hoveredOccurrenceIndex}
            hoveredOccurrences={hoveredOccurrences}
            hoveredPatternIDs={hoveredPatternIDs}
            id={patternHighlight.id}
            key={`${patternHighlight.id}-${segmentIndex}`}
            occurrenceIndex={segment.occurrenceIndex}
            page={segment.page}
            pattern={pattern}
            shouldShowTooltip={isHovering}
          />
        ));
      })}
    </div>
  );
};

const HoveredNoteIndicator = ({
  currentPage,
  hoveredNote,
  hoveredNoteContext
}: {
  currentPage: number;
  hoveredNote: SVGGElement;
  hoveredNoteContext: NoteContext;
}) => {
  const [theme] = useContext(ThemeContext);

  if (hoveredNote === undefined) return null;

  hoveredNote = hoveredNote.querySelector('g.vf-notehead path');
  const page = document.querySelector(getPageContentsSelector(currentPage));
  const { height, width, x, y } = hoveredNote.getBBox();

  if (page === null) return null;

  return createPortal(
    <AnimatedCircle
      cx={x + width / 2}
      cy={y + height / 2}
      fill={
        theme.colorByVoice
          ? theme.getVoiceColor(hoveredNoteContext.voiceNumber)
          : theme.availablePatternColors[theme.availablePatternColors.length - 1]
      }
      fillOpacity={defaultOpacity}
      r={10}
    ></AnimatedCircle>,
    page
  );
};

const addPatternHighlightContainers = (numberOfPages: number) => {
  for (let pageNumber = 1; pageNumber <= numberOfPages; pageNumber++) {
    const pageContent = document.querySelector(getPageContentsSelector(pageNumber));

    if (pageContent === null) continue;

    const patternHighlightContainer = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    patternHighlightContainer.id = `${getPageNameFromNumber(pageNumber)}-pattern-highlights`;
    // Highlights are no keyboard stops: with patterns shown, Tab stopped on the containers of every page.
    patternHighlightContainer.setAttribute('tabindex', '-1');

    pageContent.prepend(patternHighlightContainer);
  }
};

const computePatternHighlights = (
  noteElements: NoteElementSelection,
  patterns: Pattern[],
  focusingID: string | undefined
) => {
  if (patterns.length === 0) return [];

  const patternHighlights: Record<string, PatternSegments> = {};

  patterns
    .filter(
      (pattern) => pattern.isVisible && (focusingID === undefined || focusingID === pattern.id)
    )
    .forEach((pattern) => {
      pattern.occurrences.forEach((occurrence, occurrenceIndex) =>
        computePatternHighlightSegments(
          noteElements,
          pattern.color,
          patternHighlights,
          pattern.id,
          occurrence,
          occurrenceIndex
        )
      );
    });

  return Object.values(patternHighlights);
};

const computePatternHighlightSegments = (
  noteElements: NoteElementSelection,
  patternColor: string,
  patternHighlights: Record<number, PatternSegments>,
  patternID: string,
  occurrence: Occurrence,
  occurrenceIndex: number
) =>
  noteElements
    .filter((noteContext) => {
      const absoluteOffset = getAbsoluteOffset(noteContext.note.sourceNote);
      const matchesOffset = isBetweenOffsets(
        absoluteOffset,
        occurrence.firstOffset,
        occurrence.lastOffset
      );
      const occursInVoice = occurrence.voiceNumber === noteContext.voiceNumber;

      return occursInVoice && matchesOffset;
    })
    .sort(
      (firstNoteContext, secondNoteContext) =>
        getAbsoluteOffset(firstNoteContext.note.sourceNote) -
        getAbsoluteOffset(secondNoteContext.note.sourceNote)
    )
    .each((noteContext, index, nodes) => {
      const node = nodes[index];
      const page = document.getElementById(`${node.viewportElement.id}-pattern-highlights`);

      const boundingBox = (node.querySelector('g.vf-notehead path') as SVGPathElement).getBBox();
      const centerX = boundingBox.x + boundingBox.width / 2;
      const centerY = boundingBox.y + boundingBox.height / 2;
      const rightX = boundingBox.x + boundingBox.width;
      const system =
        noteContext.note.parentVoiceEntry?.parentStaffEntry?.parentMeasure?.ParentMusicSystem;

      if (patternHighlights[patternID] !== undefined) {
        const numberOfSegments = patternHighlights[patternID].segments.length;
        const lastSegment = patternHighlights[patternID].segments[numberOfSegments - 1];

        // A segment stays in one line of music: the next line (or page) begins a new one, even where its first note is
        // further right than the last note of the line before (a voice that rests there), which drew a bar across.
        if (
          lastSegment.points[lastSegment.points.length - 1][0] > rightX ||
          lastSegment.page !== page ||
          (system !== undefined && lastSegment.system !== system) ||
          occurrenceIndex !==
            patternHighlights[patternID].segments[numberOfSegments - 1].occurrenceIndex
        ) {
          patternHighlights[patternID].segments.push({
            occurrenceIndex,
            page,
            points: [[centerX, centerY]],
            system
          });
        } else {
          patternHighlights[patternID].segments[numberOfSegments - 1].points.push([
            centerX,
            centerY
          ]);
        }
      } else {
        patternHighlights[patternID] = {
          color: patternColor,
          id: patternID,
          segments: [
            {
              occurrenceIndex,
              page,
              points: [[centerX, centerY]],
              system
            }
          ]
        };
      }
    });

/** The page format that fills the sheet's view (at most the visible height in the one-column layout). */
const pageFormatOf = (pages: HTMLElement) => {
  const column = pages.closest<HTMLElement>('.melodyvis-sheet-column');
  const height = Math.min(column?.clientHeight ?? window.innerHeight, window.innerHeight - 80) - 24;

  return { height: Math.max(height, 200), width: Math.max(pages.offsetWidth, 200) };
};

/**
 * The page format and OSMD zoom of a layout. OSMD makes a page as wide as its container (in units: width / zoom / 10)
 * and as high as the format's aspect ratio says: an A4 page is laid out a4PageUnits wide whatever the view, a fitted
 * page fills the view at the zoom.
 */
const applyPageLayout = (
  openSheetMusicDisplay: OSMD,
  pages: HTMLElement,
  mode: PageMode,
  size: { height: number; width: number },
  zoom: number
) => {
  const rules = openSheetMusicDisplay.EngravingRules;
  const margins = pageMargins[mode];
  rules.PageLeftMargin = margins.side + (hasBrackets(openSheetMusicDisplay) ? bracketWidth : 0);
  rules.PageRightMargin = margins.side;
  rules.PageBottomMargin = margins.bottom;
  // 'compacttight' places the first staff of a page at the narrow top margin.
  rules.PageTopMargin = margins.top;
  rules.PageTopMarginNarrow = margins.top;
  // The title starts at the top margin; OSMD puts the first staff at the top margin plus the title's distances.
  rules.TitleTopDistance = margins.top;
  rules.TitleBottomDistance = rules.RenderSubtitle
    ? titleGap - margins.top + rules.SheetMinimumDistanceBetweenTitleAndSubtitle + bylineHeight
    : titleGap - margins.top;
  if (mode === 'a4') {
    // On the canvas the pages take no width from the view (layout.css): OSMD, which clears them before it measures,
    // gets one of its own.
    pages.style.width = `${Math.max(pages.parentElement?.clientWidth ?? 0, 600)}px`;
    openSheetMusicDisplay.Zoom = pages.offsetWidth / (10 * a4PageUnits);
    openSheetMusicDisplay.setCustomPageFormat(210, 297);
  } else {
    openSheetMusicDisplay.Zoom = zoom;
    openSheetMusicDisplay.setCustomPageFormat(size.width, size.height);
  }
};

const yearPattern = /^(1[4-9]\d\d|20\d\d)$/;

/**
 * The year of the piece if its MusicXML names one: a credit (or work, movement title) that is only a year, as printed
 * under a title ("1722"), or a year in parentheses after the work ("Drei Klavierstücke, Op. 11 (1909)"). Life dates
 * ("(1685 - 1750)") and the encoding date are no years of the piece.
 */
export const yearOf = (musicXML: string | undefined): string | undefined => {
  if (!musicXML) return undefined;
  const texts = Array.from(
    musicXML.matchAll(/<(credit-words|work-title|movement-title)\b[^>]*>([^<]*)</g),
    (match) => match[2].trim()
  );

  return (
    texts.find((text) => yearPattern.test(text)) ??
    texts.map((text) => text.match(/\((1[4-9]\d\d|20\d\d)\)/)?.[1]).find(Boolean)
  );
};

/** What the first page names above the music: the title, and the artist and year where they are known. */
export const titleBlockOf = (musicSheet: MusicSheet) => {
  const composer = musicSheet.composer?.trim();
  const artist = composer && !/^unknown$/i.test(composer) ? composer : undefined;

  return {
    byline: [artist, yearOf(musicSheet.musicXML)].filter(Boolean).join(' · '),
    title: musicSheet.title?.trim() ?? ''
  };
};

/**
 * OSMD draws the title block on the first page, centred above the music: the catalogue's title, below it the artist
 * and the year (the MusicXML's own credits stay off, they repeat or contradict the catalogue). Part of the page's SVG,
 * so it scales and moves with the page and the highlights stay on their notes.
 */
const showTitleBlock = (openSheetMusicDisplay: OSMD, musicSheet: MusicSheet) => {
  const { byline, title } = titleBlockOf(musicSheet);
  const rules = openSheetMusicDisplay.EngravingRules;
  const sheet = openSheetMusicDisplay.Sheet;
  rules.SheetTitleHeight = titleHeight;
  rules.SheetSubtitleHeight = bylineHeight;
  rules.RenderTitle = title.length > 0;
  rules.RenderSubtitle = title.length > 0 && byline.length > 0;
  rules.RenderComposer = false;
  rules.RenderLyricist = false;
  rules.RenderCopyright = false;
  if (title) sheet.TitleString = title;
  if (byline) sheet.SubtitleString = byline;
};

/** Renders the sheet at its original size, on A4 pages or on pages that fill the view; returns the page size. */
const displayMusicSheet = async (
  musicSheet: MusicSheet | undefined,
  musicSheetContainer: HTMLDivElement,
  setOpenSheetMusicDisplay: Dispatch<SetStateAction<OSMD>>,
  mode: PageMode
) => {
  if (musicSheet) {
    const openSheetMusicDisplay: OSMD = new OSMD(musicSheetContainer, {
      autoResize: false,
      drawingParameters: 'compacttight'
    });

    await openSheetMusicDisplay.load(musicSheet.musicXML);
    showTitleBlock(openSheetMusicDisplay, musicSheet);

    const size = pageFormatOf(musicSheetContainer);
    applyPageLayout(openSheetMusicDisplay, musicSheetContainer, mode, size, 1);
    openSheetMusicDisplay.render();
    setOpenSheetMusicDisplay(openSheetMusicDisplay);

    return size;
  }
};

/** The page of the first note at or after the offset. */
const pageWithOffset = (elements: NoteElementSelection, offset: number) => {
  let first: { offset: number; page: number } | undefined;
  elements.each(function (noteContext) {
    const noteOffset = getAbsoluteOffset(noteContext.note.sourceNote);
    if (noteOffset < offset - 1e-6 || (first && first.offset <= noteOffset)) return;
    first = { offset: noteOffset, page: Number(this.ownerSVGElement.id.match(/\d+/)[0]) };
  });

  return first?.page;
};

const getNoteElements = (
  openSheetMusicDisplay: OSMD,
  voices: Voice[],
  voiceColoringHitSet: Set<MeasureElement>,
  setAbsoluteOffsetPageMap: Dispatch<SetStateAction<Map<number, number>>>,
  setVoiceColoringHitSet: Dispatch<SetStateAction<Set<MeasureElement>>>
) => {
  const absoluteOffsetPageMap = new Map<number, number>();
  const elements: SVGGElement[] = [];
  const noteContexts: NoteContext[] = [];

  openSheetMusicDisplay.GraphicSheet.MeasureList.forEach((system) => {
    system.forEach((measure) => {
      // Staves without a measure here and notes OSMD does not draw individually (e.g. inside multi-measure rests)
      // have no SVG element; they crashed the app (audit finding F17, Symphony No. 5).
      measure?.staffEntries.forEach((staffEntry) => {
        staffEntry.graphicalVoiceEntries.forEach((graphicalVoiceEntry) => {
          graphicalVoiceEntry.notes.forEach((note) => {
            const vexFlowNote: VexFlowGraphicalNote = note as VexFlowGraphicalNote;
            const element: SVGGElement | undefined = vexFlowNote.vfnote?.[0]?.getAttribute('el');
            if (!element?.ownerSVGElement) return;

            const voiceNumber = getVoiceNumber(
              voices,
              note.sourceNote,
              measure.MeasureNumber,
              voiceColoringHitSet
            );

            const pageNumber = element.ownerSVGElement.id.match(/\d+/)[0];
            const absoluteNoteOffset = getAbsoluteOffset(note.sourceNote);
            absoluteOffsetPageMap.set(absoluteNoteOffset, Number(pageNumber));

            elements.push(element);
            // FIXME: coloring does not work for chord notes other than the root.
            noteContexts.push({ note, voiceNumber });
          });
        });
      });
    });
  });

  setAbsoluteOffsetPageMap(absoluteOffsetPageMap);
  setVoiceColoringHitSet(new Set(voiceColoringHitSet));

  return selectAll(elements).data(noteContexts);
};

export const getPageContentsSelector = (pageNumber: number) =>
  `${getPageIDFromNumber(pageNumber)} > g`;
export const getPageIDFromNumber = (pageNumber: number) => `#${getPageNameFromNumber(pageNumber)}`;
const getPageNameFromNumber = (pageNumber: number) => `osmdSvgPage${pageNumber}`;

const getShortestPatternIDFromIDs = (patternIDs: string[], patterns: Pattern[]) =>
  patterns
    .filter((pattern) => patternIDs.includes(pattern.id))
    .sort((firstPattern, secondPattern) => firstPattern.length - secondPattern.length)[0].id;

const getVoiceNumber = (
  ofVoices: Voice[],
  forNote: Note,
  inMeasureNumber: number,
  voiceColoringHitSet: Set<MeasureElement>
) =>
  ofVoices.find((voice) =>
    voice.measures
      .filter((measure) => measure.number === inMeasureNumber)
      .some((measure) =>
        measure.elements.some((element) => {
          // We need the hit set to avoid elements with the same duration/offset/pitch being matched to the same voice.
          if (voiceColoringHitSet.has(element)) return false;

          if (isChord(forNote)) {
            if (Array.isArray(element.pitch) && getChordSize(forNote) === element.pitch.length) {
              const chordNotesAlign = forNote.ParentVoiceEntry.Notes.every((note, index) => {
                const notePitch = `${translateNoteEnumToString(
                  note.Pitch.FundamentalNote
                )}${translateAccidentalEnumToString(note.Pitch.Accidental)}${
                  note.Pitch.Octave + 3
                }`;
                const elementPitch = element.pitch[index];

                return notePitch === elementPitch;
              });

              if (!chordNotesAlign) return false;
            } else {
              return false;
            }
          } else {
            const forNotePitch = getPitch(forNote);

            if (!((forNote.isRest() && element.pitch === null) || forNotePitch === element.pitch))
              return false;
          }

          const forNoteDuration = getDuration(forNote);
          const forNoteOffset = getOffset(forNote);

          const matchesDuration = forNote.IsGraceNote || forNoteDuration === element.duration;
          const matchesOffset = forNoteOffset === element.offset;

          const matches = matchesDuration && matchesOffset;
          // For an unknown reason, chords are not colored correctly if included in the hit set.
          if (matches && !Array.isArray(element.pitch)) {
            voiceColoringHitSet.add(element);
          }

          return matches;
        })
      )
  )?.number;

/**
 * Crops each fitted page to its music (the last page ends early; an A4 page stays a whole sheet) and groups its
 * contents for the note selection.
 */
const preparePages = (numberOfPages: number, mode: PageMode) => {
  for (let pageNumber = 1; pageNumber <= numberOfPages; pageNumber++) {
    const pageContent = document.querySelector(getPageIDFromNumber(pageNumber));

    if (pageContent === null) continue;

    if (mode === 'fit') {
      const boundingBox = (pageContent as SVGGraphicsElement).getBBox();
      const svgViewBox = (pageContent as SVGElement).getAttribute('viewBox').split(' ');

      // Cropped at the scale the page was laid out at: its height shrinks, its notes keep their size. Below the music
      // stays the page's bottom margin (the view box counts 10 per staff space).
      const contentHeight = Math.min(
        Number(svgViewBox[3]),
        boundingBox.y + boundingBox.height + 10 * pageMargins.fit.bottom
      );
      const scale = Number(pageContent.getAttribute('width')) / Number(svgViewBox[2]);
      (pageContent as SVGGraphicsElement).setAttribute(
        'viewBox',
        `${svgViewBox[0]} ${svgViewBox[1]} ${svgViewBox[2]} ${contentHeight}`
      );
      if (scale > 0) pageContent.setAttribute('height', String(contentHeight * scale));
    }

    // Group music sheet elements to decrease the part of the user interface that allows for note selection.
    const pageContents = Array.from(
      document.querySelectorAll(`${getPageIDFromNumber(pageNumber)} > *`)
    );
    // Remove elements from the page's SVG.
    pageContents.forEach((element) => element.remove());

    // Create a group that contains the music sheet elements.
    const pageContentsContainer = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    pageContentsContainer.classList.add('cursor-pointer', 'background-white', 'focus:outline-0');
    pageContentsContainer.style.pointerEvents = 'bounding-box';

    document.querySelector(getPageIDFromNumber(pageNumber)).append(pageContentsContainer);
    // Add elements to the page's container.
    document.querySelector(`${getPageIDFromNumber(pageNumber)} > g`).append(...pageContents);
  }
};

export default MelodyAnalysis;
